import { useState, useRef, useEffect, useCallback } from "react";
import { Drawer } from "vaul";
import { useSwipeable } from "./useSwipeable";
import { renderLatexToElement } from "@/lib/katex-config";
import { KEYS, KEYPAD_TABS, handleKeyPress, type KeypadTab } from "./calculator-keys";
import type { FormulaCalculatorProps } from "./FormulaCalculator";
import "katex/dist/katex.min.css";

/**
 * Mobile: Bottom-sheet calculator with native-feeling controls.
 * - Vaul drawer slides up like native keyboard
 * - Swipe left/right to switch tabs
 * - Haptic feedback on button press
 * - Large touch targets (52px)
 * - Segmented control for tab categories
 */

const TAB_GROUPS = [
  { label: "Math", tabs: ["123", "ops", "struct", "func"] as KeypadTab[] },
  { label: "Letters", tabs: ["abc", "greek"] as KeypadTab[] },
  { label: "Science", tabs: ["phys", "chem", "stat", "eng"] as KeypadTab[] },
];

function haptic() {
  if (navigator.vibrate) navigator.vibrate(8);
}

export function FormulaCalculatorMobile({ value, onChange, onClose }: FormulaCalculatorProps) {
  const [activeTab, setActiveTab] = useState<KeypadTab>("123");
  const [activeGroup, setActiveGroup] = useState(0);
  const [latex, setLatex] = useState(value);
  const previewRef = useRef<HTMLDivElement>(null);
  const [showSource, setShowSource] = useState(false);

  useEffect(() => { setLatex(value); }, [value]);

  // Render preview
  useEffect(() => {
    if (!previewRef.current) return;
    if (!latex.trim()) {
      previewRef.current.innerHTML = '<span style="color:var(--lilia-text-muted);opacity:0.6;font-size:15px">Tap buttons to build</span>';
      return;
    }
    try { renderLatexToElement(latex, previewRef.current, { displayMode: true }); } catch { /* */ }
  }, [latex]);

  const handleKey = useCallback((key: typeof KEYS["123"][0]) => {
    haptic();
    setLatex((prev) => handleKeyPress(key, prev, onChange));
  }, [onChange]);

  // Swipe to change tabs within current group
  const currentGroupTabs = TAB_GROUPS[activeGroup].tabs;
  const currentTabIndex = currentGroupTabs.indexOf(activeTab);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      const next = currentTabIndex + 1;
      if (next < currentGroupTabs.length) {
        setActiveTab(currentGroupTabs[next]);
        haptic();
      } else if (activeGroup < TAB_GROUPS.length - 1) {
        // Jump to next group
        setActiveGroup(activeGroup + 1);
        setActiveTab(TAB_GROUPS[activeGroup + 1].tabs[0]);
        haptic();
      }
    },
    onSwipedRight: () => {
      const prev = currentTabIndex - 1;
      if (prev >= 0) {
        setActiveTab(currentGroupTabs[prev]);
        haptic();
      } else if (activeGroup > 0) {
        setActiveGroup(activeGroup - 1);
        const prevGroupTabs = TAB_GROUPS[activeGroup - 1].tabs;
        setActiveTab(prevGroupTabs[prevGroupTabs.length - 1]);
        haptic();
      }
    },
  });

  const handleGroupChange = (idx: number) => {
    setActiveGroup(idx);
    setActiveTab(TAB_GROUPS[idx].tabs[0]);
    haptic();
  };

  return (
    <Drawer.Root open={true} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/40 z-[99]" />
        <Drawer.Content className="fixed bottom-0 left-0 right-0 z-[100] flex flex-col bg-[var(--lilia-bg,#fff)] rounded-t-2xl max-h-[92vh] outline-none">
          {/* Drag handle */}
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 rounded-full bg-[var(--lilia-border,#d1d5db)]" />
          </div>

          {/* Preview area — tappable to show/hide source */}
          <div
            className="min-h-[64px] max-h-[120px] overflow-auto flex items-center justify-center px-5 py-3"
            onClick={() => setShowSource(!showSource)}
          >
            <div ref={previewRef} className="[&_.katex]:text-[22px] text-center w-full" />
          </div>

          {/* Source editor — collapsible */}
          {showSource && (
            <div className="px-4 py-2 border-y border-[var(--lilia-border)] bg-[var(--lilia-surface,#f8fafc)]">
              <textarea
                value={latex}
                onChange={(e) => { setLatex(e.target.value); onChange(e.target.value); }}
                className="w-full text-[13px] font-mono text-[var(--lilia-text)] bg-transparent resize-none focus:outline-none"
                rows={2}
                placeholder="LaTeX..."
              />
              <div className="flex justify-end gap-2 mt-1">
                <button
                  onClick={() => { setLatex(""); onChange(""); }}
                  className="text-[11px] text-[var(--lilia-text-muted)] px-3 py-1 rounded-full bg-[var(--lilia-surface)] active:bg-[var(--lilia-border)]"
                >Clear</button>
              </div>
            </div>
          )}

          {/* Segmented control — group selector (Math / Letters / Science) */}
          <div className="flex mx-3 my-2 p-0.5 rounded-lg bg-[var(--lilia-surface,#f1f5f9)]">
            {TAB_GROUPS.map((group, idx) => (
              <button
                key={group.label}
                className={`flex-1 py-2 text-[12px] font-semibold rounded-md transition-all ${
                  activeGroup === idx
                    ? "bg-[var(--lilia-bg,#fff)] text-[var(--lilia-text,#334155)] shadow-sm"
                    : "text-[var(--lilia-text-muted,#94a3b8)]"
                }`}
                onClick={() => handleGroupChange(idx)}
              >
                {group.label}
              </button>
            ))}
          </div>

          {/* Sub-tabs within group */}
          <div className="flex mx-3 mb-1 gap-1">
            {currentGroupTabs.map((tab) => {
              const tabMeta = KEYPAD_TABS.find((t) => t.key === tab);
              return (
                <button
                  key={tab}
                  className={`flex-1 py-1.5 text-[11px] font-bold rounded-md transition-all ${
                    activeTab === tab
                      ? "text-[var(--lilia-primary,#0A76A4)] bg-[var(--lilia-primary,#0A76A4)]/8"
                      : "text-[var(--lilia-text-muted,#94a3b8)]"
                  }`}
                  onClick={() => { setActiveTab(tab); haptic(); }}
                >
                  {tabMeta?.icon || tabMeta?.label || tab}
                </button>
              );
            })}
          </div>

          {/* Keypad — swipeable, 5 columns */}
          <div
            {...swipeHandlers}
            className="grid grid-cols-5 gap-[3px] px-1.5 pb-6 pt-1 overflow-y-auto max-h-[50vh]"
          >
            {KEYS[activeTab].map((key, i) => (
              <button
                key={`${key.latex}-${i}`}
                onClick={() => handleKey(key)}
                className={`
                  flex items-center justify-center rounded-xl
                  text-[var(--lilia-text,#334155)]
                  active:scale-90 active:bg-[var(--lilia-primary,#0A76A4)] active:text-[color:var(--lilia-on-primary)]
                  transition-all duration-75
                  ${key.wide ? "col-span-2" : ""}
                  ${key.latex === "__BACKSPACE__"
                    ? "bg-[var(--lilia-border,#d1d5db)]"
                    : "bg-[var(--lilia-surface,#f1f5f9)]"
                  }
                  h-[52px]
                `}
              >
                <span className={`leading-none select-none ${
                  activeTab === "123" ? "text-[22px] font-bold" : ""
                } ${
                  activeTab === "greek" ? "text-[20px] font-serif font-bold" : ""
                } ${
                  ["ops", "struct", "phys", "chem", "eng"].includes(activeTab) ? "text-[14px] font-bold" : ""
                } ${
                  ["func", "abc", "stat"].includes(activeTab) ? "text-[13px] font-semibold" : ""
                }`}>
                  {key.label}
                </span>
              </button>
            ))}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
